import { UnitInput } from "@/components/primitives/unit-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { NutritionDraft, WaterMode } from "./types";
import { cn } from "@/lib/utils";
import { formatPercentage } from "@/lib/formatters";

type SharedNutrientFieldsProps = {
  draft: NutritionDraft;
  onChange: (field: keyof NutritionDraft, value: number) => void;
  onWaterModeChange: (mode: WaterMode) => void;
  includeAddedSugars?: boolean;
  estimatedWaterPct: number;
  estimatedWaterGrams: number;
  errors?: Record<string, string>;
};

export function NutrientFields({
  draft,
  onChange,
  onWaterModeChange,
  includeAddedSugars,
  estimatedWaterPct,
  estimatedWaterGrams,
  errors = {},
}: SharedNutrientFieldsProps) {
  return (
    <div className="space-y-3">
      <div className="divide-y border-2 px-2 md:px-4">
        <NutrientRow
          label="Serving Size"
          unit="g"
          value={draft.gramsPerServing}
          onChange={(value) => onChange("gramsPerServing", value)}
          error={errors.gramsPerServing}
          bold
          thickBorder
        />
        <NutrientRow
          label="Calories"
          unit="kcal"
          value={draft.caloriesPerServing}
          onChange={(value) => onChange("caloriesPerServing", value)}
          error={errors.caloriesPerServing}
          bold
          thickBorder
        />
        <NutrientRow
          label="Total Fat"
          unit="g"
          value={draft.fatPerServing}
          onChange={(value) => onChange("fatPerServing", value)}
          error={errors.fatPerServing}
          bold
        />
        <NutrientRow
          label="Saturated Fat"
          unit="g"
          value={draft.saturatedFatPerServing}
          onChange={(value) => onChange("saturatedFatPerServing", value)}
          error={errors.saturatedFatPerServing}
          indent
        />
        <NutrientRow
          label="Trans Fat"
          unit="g"
          value={draft.transFatPerServing}
          onChange={(value) => onChange("transFatPerServing", value)}
          error={errors.transFatPerServing}
          indent
          italic
        />
        <NutrientRow
          label="Sodium"
          unit="mg"
          value={draft.sodiumMgPerServing}
          onChange={(value) => onChange("sodiumMgPerServing", value)}
          error={errors.sodiumMgPerServing}
          bold
        />
        <NutrientRow
          label="Carbohydrates"
          unit="g"
          value={draft.carbsPerServing}
          onChange={(value) => onChange("carbsPerServing", value)}
          error={errors.carbsPerServing}
          bold
        />
        <NutrientRow
          label="Sugars"
          unit="g"
          value={draft.sugarsPerServing}
          onChange={(value) => onChange("sugarsPerServing", value)}
          error={errors.sugarsPerServing}
          indent
        />
        {includeAddedSugars ? (
          <NutrientRow
            label="Added Sugars"
            unit="g"
            value={draft.addedSugarsPerServing}
            onChange={(value) => onChange("addedSugarsPerServing", value)}
            error={errors.addedSugarsPerServing}
            indent
            doubleIndent
          />
        ) : null}
        <NutrientRow
          label="Fiber"
          unit="g"
          value={draft.fiberPerServing}
          onChange={(value) => onChange("fiberPerServing", value)}
          error={errors.fiberPerServing}
          indent
        />
        <NutrientRow
          label="Protein"
          unit="g"
          value={draft.proteinPerServing}
          onChange={(value) => onChange("proteinPerServing", value)}
          error={errors.proteinPerServing}
          bold
        />
      </div>
      <div className="space-y-1.5">
        <span className="text-sm font-bold">Water</span>
        <p className="text-muted-foreground text-xs">
          Estimated from remaining mass. Actual water content may be lower due
          to non-nutritional components (known as <em>ash</em>).
        </p>
        <RadioGroup
          value={draft.waterMode}
          onValueChange={(value) => {
            if (value === "estimated" || value === "manual") {
              onWaterModeChange(value);
            }
          }}
          className="gap-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="estimated" id="water-estimated" />
            <Label htmlFor="water-estimated" className="text-sm font-normal">
              Use estimated water content: {estimatedWaterGrams}g (
              {formatPercentage(estimatedWaterPct / 100, 0, 1)})
            </Label>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="manual" id="water-manual" />
              <Label htmlFor="water-manual" className="text-sm font-normal">
                Specify water content
              </Label>
            </div>
            {draft.waterMode === "manual" && (
              <div className="pl-6">
                <UnitInput
                  value={draft.waterGrams}
                  unit="g"
                  onChange={(e) =>
                    onChange("waterGrams", e.target.valueAsNumber || 0)
                  }
                  min={0}
                  className="w-28"
                />
              </div>
            )}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

type NutrientRowProps = {
  label: string;
  unit: "%" | "g" | "kcal/g" | "mg" | "°C" | "kcal";
  value: number;
  onChange: (value: number) => void;
  error?: string;
  bold?: boolean;
  indent?: boolean;
  doubleIndent?: boolean;
  italic?: boolean;
  thickBorder?: boolean;
};

function NutrientRow({
  label,
  unit,
  value,
  onChange,
  error,
  bold,
  indent,
  doubleIndent,
  italic,
  thickBorder,
}: NutrientRowProps) {
  return (
    <div className={cn("py-1.5", thickBorder && "border-b-4")}>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-sm",
            bold && "font-bold",
            indent && "pl-4",
            doubleIndent && "pl-8",
            italic && "italic",
          )}
        >
          {italic ? <i>{label}</i> : label}
        </span>
        <UnitInput
          value={value}
          unit={unit}
          onChange={(event) => onChange(event.target.valueAsNumber || 0)}
          min={0}
          className={cn("w-28 shrink-0", error && "border-destructive")}
        />
      </div>
      {error && (
        <p className="text-destructive mt-0.5 text-right text-xs">{error}</p>
      )}
    </div>
  );
}
